import React, { ReactElement, useState, useEffect } from "react"
import { InputProps } from "react-admin"
import Cron, {Locale } from "react-js-cron"
import "react-js-cron/dist/styles.css"
import { useTheme } from "@mui/material/styles"
import { ColorMode } from "@xyflow/react"
import { useLocale } from "ra-core"
import {  Box } from "@mui/material"
import { Global, css } from "@emotion/react"

type LocaleKey = 'en' | 'ru';
type LocalizationType = {
    [K in LocaleKey]: {
        everyText: string;
        emptyMonths: string;
        emptyMonthDays: string;
        emptyMonthDaysShort: string;
        emptyWeekDays: string;
        emptyWeekDaysShort: string;
        emptyHours: string;
        emptyMinutes: string;
        emptyMinutesForHourPeriod: string;
        yearOption: string;
        monthOption: string;
        weekOption: string;
        dayOption: string;
        hourOption: string;
        minuteOption: string;
        rebootOption: string;
        prefixPeriod: string;
        prefixMonths: string;
        prefixMonthDays: string;
        prefixWeekDays: string;
        prefixWeekDaysForMonthAndYearPeriod: string;
        prefixHours: string;
        prefixMinutes: string;
        prefixMinutesForHourPeriod: string;
        suffixMinutesForHourPeriod: string;
        errorInvalidCron: string;
        clearButtonText: string;
        weekDays: string[];
        months: string[];
        cronEvery: string;
        cronSeparator: string;
        cronRangeConnector: string;
        cronLeftBracket: string;
        cronRightBracket: string;
    }
}

const localization: LocalizationType = {
    en: {
        everyText: 'every',
        emptyMonths: 'every month',
        emptyMonthDays: 'every day of the month',
        emptyMonthDaysShort: 'every day of the month',
        emptyWeekDays: 'every day of the week',
        emptyWeekDaysShort: 'every day of the week',
        emptyHours: 'every hour',
        emptyMinutes: 'every minute',
        emptyMinutesForHourPeriod: 'every',
        yearOption: 'year',
        monthOption: 'month',
        weekOption: 'week',
        dayOption: 'day',
        hourOption: 'hour',
        minuteOption: 'minute',
        rebootOption: 'reboot',
        prefixPeriod: 'Every',
        prefixMonths: 'in',
        prefixMonthDays: 'on',
        prefixWeekDays: 'on',
        prefixWeekDaysForMonthAndYearPeriod: 'and',
        prefixHours: 'at',
        prefixMinutes: ':',
        prefixMinutesForHourPeriod: 'at',
        suffixMinutesForHourPeriod: 'minute(s)',
        errorInvalidCron: 'Invalid cron expression',
        clearButtonText: 'Clear',
        weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        cronEvery: 'every',
        cronSeparator: ',',
        cronRangeConnector: '-',
        cronLeftBracket: '[',
        cronRightBracket: ']',
    },
    ru: {
        everyText: 'каждый',
        emptyMonths: 'каждый месяц',
        emptyMonthDays: 'каждый день месяца',
        emptyMonthDaysShort: 'каждый день месяца',
        emptyWeekDays: 'каждый день недели',
        emptyWeekDaysShort: 'каждый день недели',
        emptyHours: 'каждый час',
        emptyMinutes: 'каждую минуту',
        emptyMinutesForHourPeriod: 'каждую',
        yearOption: 'год',
        monthOption: 'месяц',
        weekOption: 'неделю',
        dayOption: 'день',
        hourOption: 'час',
        minuteOption: 'минуту',
        rebootOption: 'перезагрузка',
        prefixPeriod: 'Каждый',
        prefixMonths: 'в',
        prefixMonthDays: '',
        prefixWeekDays: 'в',
        prefixWeekDaysForMonthAndYearPeriod: 'и',
        prefixHours: 'в',
        prefixMinutes: ':',
        prefixMinutesForHourPeriod: 'в',
        suffixMinutesForHourPeriod: 'минуту(ы)',
        errorInvalidCron: 'Некорректное CRON-выражение',
        clearButtonText: 'Очистить',
        weekDays: ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'],
        months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        cronEvery: 'каждый',
        cronSeparator: ',',
        cronRangeConnector: '-',
        cronLeftBracket: '[',
        cronRightBracket: ']',
    }
}

interface CronWidgetProps  {
  defaultValue?: string
  value?: string
  setValue?: (value: string) => void
  readOnly?: boolean
  hideBox?: boolean
  label?: string | ReactElement | false
  resource?: string
  source: string
}

const CronWidget: React.FC<CronWidgetProps> = (props) => {
  const {
    defaultValue = '* * * * *',
    setValue=() => {},
    value,
    label,
    source,
    resource,
    readOnly = false,
    hideBox = false,
    ...rest
  } = props
  
  const localeApp = useLocale()
  const [locale, setLocale] = useState<LocaleKey>(localeApp as LocaleKey)
  const theme = useTheme()
  const appColorMode = theme.palette.mode
  const [colorMode, setColorMode] = useState<ColorMode>(appColorMode)
  const isDark = colorMode === "dark"

  useEffect(() => {
    setColorMode(appColorMode)
  }, [appColorMode])

  useEffect(() => {
    setLocale(localeApp as LocaleKey)
  }, [localeApp])

  const getLocaleData = () => {
    return localization[locale] || localization['en']
  }

  // Define global styles for Ant Design components
  const globalStyles = css`
    .ant-select-dropdown {
      background-color: ${isDark ? '#424242' : '#ffffff'} !important;
      color: ${isDark ? '#ffffff' : '#000000'} !important;
      border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'};
    }
    
    .ant-select-item {
      color: ${isDark ? '#e0e0e0' : '#333333'} !important;
    }
    
    .ant-select-item-option-active, 
    .ant-select-item-option-selected {
      background-color: ${isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)'} !important;
    }
    
    .ant-select-selection-item {
      color: ${isDark ? '#e0e0e0' : '#333333'} !important;
      background-color: ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'} !important;
    }
    
    .ant-select-selection-placeholder {
      color: ${isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'} !important;
    }
    
    .ant-select {
      color: ${isDark ? '#e0e0e0' : '#333333'} !important;
    }
    
    .ant-select-selector {
      background-color: ${isDark ? '#333333' : '#f5f5f5'} !important;
      border-color: ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'} !important;
    }
  `;

  return (
    <Box>
      {/* Include global styles using emotion */}
      <Global styles={globalStyles} />
      <Box 
        sx={{
          bgcolor: hideBox ? undefined : (isDark ? 'background.paper' : 'background.default'),
          p: hideBox ? undefined : 2,
          borderRadius: hideBox ? undefined : 1,
          border: hideBox ? undefined : (`1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`),
          '& .react-js-cron': {
            color: theme.palette.text.primary,
            width: '100%',
            '& .react-js-cron-error': {
              color: theme.palette.error.main,
              '& .react-js-cron-custom-select': {
                background: isDark ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)',
              }
            },
            '& .react-js-cron-custom-select': {
              color: theme.palette.text.primary,
              '& > div': {
                bgcolor: 'transparent',
              }
            },
            '& .ant-select-selection-overflow': {
              display: 'contents',
              flex: 0,
            },
            '& .ant-select-item-option-selected': {
              background: 'transparent !important',
            },
            
            '& .ant-select-selection-item': {
              background: 'transparent !important',
            },
            '& button': {
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              padding: '6px 16px',
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              }
            }
          }
        }}
      >
        <Cron
          value={value || defaultValue}
          setValue={setValue}
          clearButton={!readOnly}
          displayError={false}
          readOnly={readOnly}
          allowEmpty='never'
          humanizeValue
          //humanizeLabels={false}
          locale={getLocaleData() as Locale}
          {...rest}
        />
      </Box>
    </Box>
  )
}

export default CronWidget